import { ScatterplotLayer, TextLayer } from '@deck.gl/layers/typed'
import { getDistanceFromLatLon } from '@shared/lib'

import { ClusterModel } from '../../../entities/cluster/model/domain/cluster.model'
import {
  LAYER_CIRCLE_COLOR,
  LAYER_CIRCLE_STROKE,
  LAYER_NAME,
  LAYER_TEXT_COLOR
} from './config'

/** Вычисляет смещение в пробелах (не в пикселях) между иконкой и текстом */
const getIconFontStrByDigitLength = (pointsCountDigit: number): string => {
  let resultStr = '\ue7fd '
  const digitStr = `${pointsCountDigit}`
  const digitStrLength = digitStr.length
  for (let i = 0; i < digitStrLength; i++) {
    resultStr = resultStr + ' '
  }
  return resultStr
}

/** Созданние слоя с кластерами для Deck.Gl с заданным Id, прозрачностью и данными. Если круг выбран пользователем - его радиус = 0 и на его место поместится другим слоем новый круг. */
export const computeClustersLayer = (
  name: string,
  circlesData: ClusterModel[],
  opacity: number
): ScatterplotLayer => {
  return new ScatterplotLayer({
    id: name,
    data: circlesData,
    opacity: opacity,
    stroked: false,
    filled: true,
    getPosition: (d: ClusterModel) => d.coordinates,
    getRadius: (d) => d.radius,
    getFillColor: () => LAYER_CIRCLE_COLOR.PRIMARY
  })
}

/** Создание слоя с отрисовкой выбранного кластера (поверх кластера из computeClustersLayer) */
export const computeSelectedCircleLayer = (
  selectedCluster: ClusterModel | null
): ScatterplotLayer | null => {
  if (!selectedCluster) {
    return null
  }
  return new ScatterplotLayer({
    id: LAYER_NAME.CLUSTER,
    data: [selectedCluster],
    opacity: 0.15,
    stroked: true,
    filled: true,
    // lineWidthMinPixels: 2,
    getPosition: (d) => d.coordinates,
    getRadius: (d) => d.radius,
    getFillColor: () => LAYER_CIRCLE_COLOR.SELECTED,
    getLineColor: () => LAYER_CIRCLE_STROKE.SELECTED
  })
}

/** Созданние слоя с икноками на кластерах (поверх кластера из computeClustersLayer) */
export const computeClustersIconLayer = (
  circlesLayerData: ClusterModel[],
  layerId: string,
  _currentZoom: number
): TextLayer | null => {
  if (!circlesLayerData) {
    return null
  }
  return new TextLayer({
    id: layerId,
    data: circlesLayerData,
    // visible: layerId !== LAYER_NAME.ICON_LITTLE && currentZoom > 14,
    getPosition: (d: ClusterModel) => d.coordinates,
    characterSet: ['\ue7fd'],
    getText: (d: ClusterModel) => getIconFontStrByDigitLength(d.usersCount),
    getSize: (d: ClusterModel) => d.fontSize,
    getTextAnchor: 'middle',
    getPixelOffset: (d) => [0, 4 * d.fontSize * 0.04],
    getColor: () => LAYER_TEXT_COLOR.PRIMARY,
    getAlignmentBaseline: 'center',
    fontFamily: 'Material Icons'
  })
}

/** Созданние слоя с текстовой подписью на кластерах (поверх кластера из computeClustersLayer) */
export const computeClustersTextLayer = (
  circlesLayerData: ClusterModel[],
  layerId: string,
  _currentZoom: number
): TextLayer | null => {
  if (!circlesLayerData) {
    return null
  }
  return new TextLayer({
    id: layerId,
    data: circlesLayerData,
    getPosition: (d: ClusterModel) => d.coordinates,
    getText: (d: ClusterModel) => `    ${d.usersCount}`,
    // visible: currentZoom > 12,
    getSize: (d: ClusterModel) => d.fontSize,
    getTextAnchor: 'middle',
    getColor: () => LAYER_TEXT_COLOR.PRIMARY,
    getAlignmentBaseline: 'center',
    fontFamily: 'Calibri',
    fontWeight: 'bold'
  })
}

/** Поиск круга среди clustersData, который содержит в себе точку (clickLat, clickLon) */
export const getClustersContainingClickPoint = (
  clickLat: number,
  clickLon: number,
  clustersData: ClusterModel[]
): ClusterModel | null => {
  const foundClusters = clustersData.filter((cluster: ClusterModel) => {
    const distFromPointToCenter = getDistanceFromLatLon(
      clickLat,
      clickLon,
      cluster.coordinates[1],
      cluster.coordinates[0]
    )
    return distFromPointToCenter < cluster.radius
  })
  if (foundClusters && foundClusters.length >= 1) {
    return foundClusters[0]
  }
  return null
}

/** Проверка факта того, кликнул ли пользователь на какой-либо кластер */
export const isClusterClicked = (
  clickLat: number,
  clickLon: number,
  bigCirclesDeckLayerData: ClusterModel[],
  mediumCirclesDeckLayerData: ClusterModel[],
  littleCirclesDeckLayerData: ClusterModel[]
): ClusterModel | undefined => {
  if (bigCirclesDeckLayerData) {
    const foundBigCluster = getClustersContainingClickPoint(
      clickLat,
      clickLon,
      bigCirclesDeckLayerData
    )
    if (foundBigCluster) {
      return foundBigCluster
    }
  }
  if (mediumCirclesDeckLayerData) {
    const foundMediumCluster = getClustersContainingClickPoint(
      clickLat,
      clickLon,
      mediumCirclesDeckLayerData
    )
    if (foundMediumCluster) {
      return foundMediumCluster
    }
  }
  if (littleCirclesDeckLayerData) {
    const foundLittleCluster = getClustersContainingClickPoint(
      clickLat,
      clickLon,
      littleCirclesDeckLayerData
    )
    if (foundLittleCluster) {
      return foundLittleCluster
    }
  }
}
